import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import MaintenancePage from './components/MaintenancePage';
import NativeTooltips from './components/NativeTooltips';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Services from './pages/Services';
import About from './pages/About';
import Contact from './pages/Contact';
import AdminLogin from './pages/Admin/Login';
import AdminDashboard from './pages/Admin/Dashboard';
import AdminClientView from './pages/Admin/ClientView';
import AdminLayout from './pages/Admin/AdminLayout';
import AdminTemplates from './pages/Admin/Templates';
import ClientManagement from './pages/Admin/ClientManagement';
import TemplateEditor from './pages/Admin/TemplateEditor';
import WikiEditor from './pages/Admin/WikiEditor';
import TmeWiki from './pages/Admin/TmeWiki';
import ScriptRepository from './pages/Admin/ScriptRepository';
import AuditTrail from './pages/Admin/AuditTrail';
import ClientLogin from './pages/Client/Login';
import ClientPortalLayout from './components/ClientPortalLayout';
import ClientDashboard from './pages/Client/Dashboard';
import ClientWiki from './pages/Client/Wiki';
import ClientAdmin from './pages/Client/Admin';
import ClientTimeline from './pages/Client/Timeline';
import ClientRisks from './pages/Client/Risks';

function Layout({ children }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isClientRoute = location.pathname.startsWith('/client');
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  // Maintenance gate: covers public pages and the client login page.
  // Admin routes are never gated (the toggle lives there). Fetch failure
  // leaves the site up - the gate must not take the site down itself.
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/client/maintenance-status`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) setMaintenanceMode(data.maintenanceMode === true);
      })
      .catch(() => {});
  }, []);

  const isClientLogin = location.pathname === '/client/login';
  const isPublicRoute = !isAdminRoute && !isClientRoute;
  if (maintenanceMode && (isPublicRoute || isClientLogin)) {
    return <MaintenancePage />;
  }

  // Admin routes - no navbar/footer
  if (isAdminRoute) {
    return <div className="min-h-screen">{children}</div>;
  }

  // Client routes - handled by ClientPortalLayout
  if (isClientRoute) {
    return children;
  }

  // Regular public pages - with navbar/footer
  return (
    <div className="min-h-screen flex flex-col bg-gray-850">
    <Navbar />
    <main className="flex-grow bg-white flex flex-col">
    {children}
    </main>
    <Footer />
    </div>
  );
}

function App() {
  return (
    <Router basename={process.env.PUBLIC_URL || '/'}>
    {/* Mounted once, outside the routes: every native title= popup in the app
        renders in the portal's own styling instead. See NativeTooltips.js. */}
    <NativeTooltips />
    <Layout>
    <Routes>
    {/* Public Routes */}
    <Route path="/" element={<Home />} />
    <Route path="/services" element={<Services />} />
    <Route path="/about" element={<About />} />
    <Route path="/contact" element={<Contact />} />

    {/* Admin Routes */}
    <Route path="/admin/login" element={<AdminLogin />} />
    <Route path="/admin/dashboard" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
    <Route path="/admin/client-view/:leadId" element={<AdminLayout><AdminClientView /></AdminLayout>} />
    <Route path="/admin/clients" element={<AdminLayout><ClientManagement /></AdminLayout>} />
    <Route path="/admin/templates" element={<AdminLayout><AdminTemplates /></AdminLayout>} />
    <Route path="/admin/templates/:templateId/edit" element={<AdminLayout><TemplateEditor /></AdminLayout>} />
    <Route path="/admin/wiki-editor" element={<AdminLayout><WikiEditor /></AdminLayout>} />
    <Route path="/admin/tme-wiki" element={<AdminLayout><TmeWiki /></AdminLayout>} />
    {/* The deep link a stored page anchor resolves to (§10.2). */}
    <Route path="/admin/tme-wiki/wikis/:wikiId/pages/:pageId" element={<AdminLayout><TmeWiki /></AdminLayout>} />
    <Route path="/admin/audit" element={<AdminLayout><AuditTrail /></AdminLayout>} />
    <Route path="/admin/scripts" element={<AdminLayout><ScriptRepository /></AdminLayout>} />
    <Route path="/admin/scripts/:scriptId" element={<AdminLayout><ScriptRepository /></AdminLayout>} />
    <Route path="/admin/scripts/:scriptId/version/:versionNumber" element={<AdminLayout><ScriptRepository /></AdminLayout>} />
    <Route path="/admin" element={<AdminLayout><AdminDashboard /></AdminLayout>} />

    {/* Client Login (separate - no portal layout) */}
    <Route path="/client/login" element={<ClientLogin />} />

    {/* Client Portal Routes (nested under ClientPortalLayout) */}
    <Route path="/client" element={<ClientPortalLayout />}>
    <Route path="project" element={<ClientDashboard />} />
    <Route path="timeline" element={<ClientTimeline />} />
    <Route path="risks" element={<ClientRisks />} />
    <Route path="wiki" element={<ClientWiki />} />
    <Route path="admin" element={<ClientAdmin />} />
    </Route>
    </Routes>
    </Layout>
    </Router>
  );
}

export default App;

